import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import APIHandler from 'config/APIHandler';

import type { DirectoryConfig } from 'hooks/useDirectoryConfig/types';
import { useDirectoryConfig } from 'hooks/useDirectoryConfig/useDirectoryConfig';
import { useDirectoryData } from 'hooks/useDirectoryData/useDirectoryData';
import { useSettings } from 'hooks/useSettings';

import CategoryFilter from './components/CategoryFilter';
import DirectoryHeader from './components/DirectoryHeader';
import DirectoryMap from './components/DirectoryMap';
import HotelsList from './components/HotelsList';
import LegacyDirectory from './components/LegacyDirectory';
import ProfileIframe from './components/ProfileIframe';
import { BREAKPOINTS, getContainerStyle } from './constants';

import 'simplebar/src/simplebar.css';
import './_directoryView.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18n-react';

const ENABLE_LOCAL_CONFIG_JSON = false; // Set to true to load config from config.json instead of API. FOR TESTING ONLY!!!

// Placeholder — useSettings is an untyped JS hook. Covers only the fields used in this file.
// Replace with the real type once useSettings is migrated to TypeScript.
interface DirectorySettings {
  appId: string;
  appInfo: { primaryColor?: string; logoWhite: string | null };
  email: string;
}

// Inner component — only rendered once config is resolved, so useDirectoryData
// always receives a non-null config and useState initializers never crash.
interface DirectoryViewContentProps {
  config: DirectoryConfig;
}

function DirectoryViewContent({ config }: DirectoryViewContentProps) {
  const { appId, appInfo, email } = useSettings() as DirectorySettings;

  useEffect(() => {
    void APIHandler.trackEvent(
      appId,
      email || 'Guest',
      'collateral',
      'view-directory',
      'view-directory',
      'view-directory',
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: fire once on mount only

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const mobile = useMemo(() => urlParams.get('mobile')?.toLowerCase() === 'true', [urlParams]);
  const header = useMemo(() => urlParams.get('header')?.toLowerCase() !== 'false', [urlParams]);
  const source = useMemo(() => urlParams.get('source')?.toLowerCase() === 'true', [urlParams]);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const containerStyle = useMemo(() => getContainerStyle(mobile), [mobile]);
  const [userInfo, setUserInfo] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const initialMapVisible = window.innerWidth > BREAKPOINTS.showMap;
  const [showMap, setShowMap] = useState(initialMapVisible);

  const {
    premiumHotelsList,
    freeHotelsList,
    categories,
    loading,
    selectedCategory,
    selectedSubcategory,
    query,
    zoom,
    center,
    language,
    lat1,
    lat2,
    lon1,
    lon2,
    setQuery,
    setSelectedCategory,
    setSelectedSubcategory,
    setZoom,
    setCenter,
    setMapVisible,
    fetchHotels,
    updateBounds,
    resetMap,
    resetFilters,
  } = useDirectoryData(config, initialMapVisible);

  const updateRightSideWidth = useCallback(() => {
    const rightSide = document.querySelector<HTMLElement>('.directoryView .rightSide');
    if (containerRef.current) {
      if (rightSide) {
        containerRef.current.style.setProperty('--right-side-width', `${rightSide.offsetWidth}px`);
      } else {
        containerRef.current.style.removeProperty('--right-side-width');
      }
    }
  }, []);

  useEffect(() => {
    updateRightSideWidth();
    window.addEventListener('resize', updateRightSideWidth);
    return () => {
      window.removeEventListener('resize', updateRightSideWidth);
    };
  }, [updateRightSideWidth]);

  useEffect(() => {
    const handleResizeCrossing = () => {
      const newWidth = window.innerWidth;
      const nowShowing = newWidth > BREAKPOINTS.showMap;
      setMapVisible(nowShowing);
      setShowMap((prev) => {
        if (prev && !nowShowing) {
          updateBounds(
            config.map.initialBounds.lat1,
            config.map.initialBounds.lat2,
            config.map.initialBounds.lon1,
            config.map.initialBounds.lon2,
            config.map.zoom,
          );
        }
        return nowShowing;
      });
    };
    window.addEventListener('resize', handleResizeCrossing);
    return () => {
      window.removeEventListener('resize', handleResizeCrossing);
    };
  }, [setMapVisible, updateBounds, config.map.initialBounds, config.map.zoom]);

  const handleInputChange = () => {
    const newQuery = searchRef.current?.value ?? '';
    setQuery(newQuery);
    void fetchHotels(lat1, lat2, lon1, lon2, newQuery, selectedCategory, selectedSubcategory);
  };

  const handleCategorySelect = (category: Parameters<typeof setSelectedCategory>[0]) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
    void fetchHotels(lat1, lat2, lon1, lon2, query, category, null);
  };

  const handleSubcategorySelect = (subcategory: Parameters<typeof setSelectedSubcategory>[0]) => {
    setSelectedSubcategory(subcategory);
    void fetchHotels(lat1, lat2, lon1, lon2, query, selectedCategory, subcategory);
  };

  const handleFiltersSubmit = (
    category: Parameters<typeof setSelectedCategory>[0],
    subcategory: Parameters<typeof setSelectedSubcategory>[0],
  ) => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    void fetchHotels(lat1, lat2, lon1, lon2, query, category, subcategory);
  };

  const handleBoundsChanged = (bounds: google.maps.LatLngBounds, newZoom: number) => {
    if (window.innerWidth <= BREAKPOINTS.showMap) return;
    updateBounds(
      bounds.getNorthEast().lat(),
      bounds.getSouthWest().lat(),
      bounds.getNorthEast().lng(),
      bounds.getSouthWest().lng(),
      newZoom,
    );
  };

  const handleMapLoad = useCallback(() => {
    updateRightSideWidth();
  }, [updateRightSideWidth]);

  const flyToMarker = (lat: number | string, lon: number | string) => {
    setZoom(config.map.flyToZoom);
    setCenter({ lat: Number(lat), lng: Number(lon) });
  };

  const buildProfileUrl = (id: string) => {
    const colorRaw = appInfo.primaryColor ?? 'e74094';
    const color = colorRaw.startsWith('#') ? colorRaw.slice(1) : colorRaw;
    return `https://cms.travpromobile.com/api/listing/detail?id=${id}&lang=${language}&color=${color}&font=Source Sans Pro&zoom=10&table=${config.listings.table}`;
  };

  const openProfile = (url: string) => {
    if (mobile) {
      window.ReactNativeWebView?.postMessage(`webview:${url}`);
    } else {
      document.getElementById('iframe')?.classList.remove('hide');
      setUserInfo(url);
    }
  };

  const closeIframe = () => {
    document.getElementById('iframe')?.classList.add('hide');
  };

  return (
    <div className="directoryContainer" ref={containerRef}>
      {source && <style>{'.cookieConsent { display: none !important; }'}</style>}
      {mobile && (
        <style>{`
          .directoryContainer { height: 100vh; }
          .cookieConsent { display: none !important; }
          .results { top: 0 !important; }
        `}</style>
      )}

      {!mobile && header && <DirectoryHeader source={source} logoWhite={appInfo.logoWhite} />}

      <div className={`directoryView${mobile ? ' mobile' : ''}`}>
        <div className="leftSide">
          <div className="results">
            {i18n.translate('directoryView.amountOfResults')}
            {freeHotelsList.length + premiumHotelsList.length}
            {!showMap && (
              <button
                className="filter-btn"
                onClick={() => {
                  setFilterOpen(!filterOpen);
                }}
              >
                {i18n.translate('directoryView.filter')}
              </button>
            )}
          </div>

          <div className="leftGrid">
            {(!mobile || !header) && (
              <div className="search">
                <FontAwesomeIcon icon={['fas', 'search']} className="searchIcon" />
                <input
                  className="searchInput"
                  placeholder={i18n.translate('directoryView.search') as string}
                  type="text"
                  ref={searchRef}
                  onChange={handleInputChange}
                />
              </div>
            )}

            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              filterOpen={filterOpen}
              onCategorySelect={handleCategorySelect}
              onSubcategorySelect={handleSubcategorySelect}
              onSubmit={handleFiltersSubmit}
              onResetFilters={resetFilters}
              onToggleFilter={() => {
                setFilterOpen(false);
              }}
              variant={!showMap ? 'inline' : config.map.useDropdownFilter ? 'dropdown' : 'checkbox'}
            />

            <HotelsList
              premiumHotelsList={premiumHotelsList}
              freeHotelsList={freeHotelsList}
              loading={loading}
              filterOpen={!showMap ? false : filterOpen}
              zoom={zoom}
              onFlyToMarker={flyToMarker}
              onPremiumInfo={(id, name) => {
                void APIHandler.trackEvent(appId, email || 'Guest', 'collateral', 'directory-listing', name, id);
                openProfile(buildProfileUrl(id));
              }}
              onFreeInfo={(id, name) => {
                void APIHandler.trackEvent(appId, email || 'Guest', 'collateral', 'directory-listing', name, id);
                openProfile(buildProfileUrl(id));
              }}
              onResetMap={resetMap}
            />
          </div>
        </div>

        {showMap && (
          <DirectoryMap
            googleMapsApiKey={config.map.googleMapsApiKey}
            containerStyle={containerStyle}
            center={center}
            zoom={zoom}
            premiumHotelsList={premiumHotelsList}
            freeHotelsList={freeHotelsList}
            onBoundsChanged={handleBoundsChanged}
            onMarkerClick={flyToMarker}
            showMarkerTooltip={config.map.showMarkerTooltip}
            onPremiumInfo={(id, name) => {
              void APIHandler.trackEvent(appId, email || 'Guest', 'collateral', 'directory-listing', name);
              openProfile(buildProfileUrl(id));
            }}
            onFreeInfo={(id, name) => {
              void APIHandler.trackEvent(appId, email || 'Guest', 'collateral', 'directory-listing', name);
              openProfile(buildProfileUrl(id));
            }}
            onMapLoad={handleMapLoad}
          />
        )}

        <ProfileIframe userInfo={userInfo} onClose={closeIframe} />
      </div>
    </div>
  );
}

// Shell — waits for config before rendering content
function DirectoryView() {
  const state = useDirectoryConfig(ENABLE_LOCAL_CONFIG_JSON);
  if (state.status === 'loading') return null;
  if (state.status === 'not_found') return <LegacyDirectory />;
  return <DirectoryViewContent config={state.config} />;
}

export default DirectoryView;
